import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutGrid,
  User,
  LogOut,
  Plus,
  Trash2,
  Pencil,
  Upload,
  Save,
  X,
  ExternalLink,
  FileText,
  Loader2,
} from "lucide-react";
import { api, resolveUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const CATEGORIES = [
  "Cybersecurity",
  "Data Analytics & Statistics",
  "Database Design",
  "AI & Cloud Computing",
  "Systems Engineering & Product Management",
  "Business Strategy & Analytics",
];

const EMPTY = {
  category: CATEGORIES[0],
  title: "",
  subtitle: "",
  description: "",
  tags: [],
  pdf_url: "",
  external_url: "",
  external_label: "",
  case_study_slug: "",
  order: 0,
};

const field =
  "w-full rounded-sm border border-[var(--border)] bg-transparent px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--accent)]";
const labelCls =
  "mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-2)]";

function ProjectForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState({
    ...EMPTY,
    ...initial,
    tags: initial?.tags || [],
    pdf_url: initial?.pdf_url || "",
    external_url: initial?.external_url || "",
    external_label: initial?.external_label || "",
    case_study_slug: initial?.case_study_slug || "",
    subtitle: initial?.subtitle || "",
    description: initial?.description || "",
  });
  const [tagsText, setTagsText] = useState((initial?.tags || []).join(", "));
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("pdf_url", data.url);
      toast.success(`Uploaded: ${data.filename}`);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    const payload = {
      ...form,
      tags: tagsText.split(",").map((t) => t.trim()).filter(Boolean),
      order: Number(form.order) || 0,
    };
    try {
      if (initial?.id) await api.put(`/projects/${initial.id}`, payload);
      else await api.post("/projects", payload);
      toast.success(initial?.id ? "Project updated" : "Project added");
      onSaved();
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-[var(--border)] bg-[var(--surface)] p-6">
      <div className="mb-5 flex items-center justify-between">
        <h3 className="font-display text-xl font-bold tracking-tight">
          {initial?.id ? "Edit Project" : "New Project"}
        </h3>
        <button onClick={onCancel} data-testid="form-cancel" className="text-[var(--text-2)] hover:text-[var(--text)]">
          <X size={18} />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className={labelCls}>Category</label>
          <select data-testid="form-category" className={field} value={form.category} onChange={(e) => set("category", e.target.value)}>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Order (within category)</label>
          <input data-testid="form-order" type="number" className={field} value={form.order} onChange={(e) => set("order", e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Title *</label>
          <input data-testid="form-title" className={field} value={form.title} onChange={(e) => set("title", e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Subtitle (course / type)</label>
          <input data-testid="form-subtitle" className={field} value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Description</label>
          <textarea data-testid="form-description" rows={4} className={field} value={form.description} onChange={(e) => set("description", e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Tags (comma separated)</label>
          <input data-testid="form-tags" className={field} value={tagsText} onChange={(e) => setTagsText(e.target.value)} />
        </div>

        <div className="md:col-span-2">
          <label className={labelCls}>PDF File</label>
          <div className="flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept=".pdf,.docx,.pptx,.png,.jpg,.jpeg,.csv,.txt" onChange={upload} className="hidden" data-testid="form-file-input" />
            <button
              type="button"
              data-testid="form-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 rounded-sm border border-[var(--border)] px-4 py-2.5 font-mono text-xs uppercase tracking-widest transition-colors hover:border-[var(--accent)] disabled:opacity-50"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? "Uploading…" : "Upload File"}
            </button>
            {form.pdf_url && (
              <a href={resolveUrl(form.pdf_url)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 font-mono text-[11px] text-[var(--accent)]">
                <FileText size={13} /> File attached <ExternalLink size={12} />
              </a>
            )}
          </div>
          <p className="mt-2 font-mono text-[10px] text-[var(--text-2)]">Or paste a URL below.</p>
          <input data-testid="form-pdf-url" className={`${field} mt-2`} placeholder="PDF URL" value={form.pdf_url} onChange={(e) => set("pdf_url", e.target.value)} />
        </div>

        <div>
          <label className={labelCls}>External URL (optional)</label>
          <input data-testid="form-external-url" className={field} placeholder="https://…" value={form.external_url} onChange={(e) => set("external_url", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>External Button Label</label>
          <input data-testid="form-external-label" className={field} placeholder="Open in Figma" value={form.external_label} onChange={(e) => set("external_label", e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Case Study Slug (optional: adds a "Read Case Study" page)</label>
          <input data-testid="form-case-study-slug" className={field} placeholder="e.g. kassam-trading" value={form.case_study_slug} onChange={(e) => set("case_study_slug", e.target.value)} />
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button data-testid="form-save" onClick={save} disabled={saving} className="flex items-center gap-2 rounded-sm bg-[var(--text)] px-5 py-3 font-mono text-xs uppercase tracking-widest text-[var(--bg)] disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
        <button onClick={onCancel} className="rounded-sm border border-[var(--border)] px-5 py-3 font-mono text-xs uppercase tracking-widest">
          Cancel
        </button>
      </div>
    </div>
  );
}

function ProfileForm() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/profile").then(({ data }) => setP(data));
  }, []);
  const set = (k, v) => setP((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", p);
      toast.success("Profile saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };
  if (!p) return <Loader2 className="animate-spin" />;
  return (
    <div className="max-w-3xl border border-[var(--border)] bg-[var(--surface)] p-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div><label className={labelCls}>Name</label><input data-testid="profile-name" className={field} value={p.name || ""} onChange={(e) => set("name", e.target.value)} /></div>
        <div><label className={labelCls}>Location</label><input data-testid="profile-location" className={field} value={p.location || ""} onChange={(e) => set("location", e.target.value)} /></div>
        <div className="md:col-span-2"><label className={labelCls}>Tagline</label><input data-testid="profile-tagline" className={field} value={p.tagline || ""} onChange={(e) => set("tagline", e.target.value)} /></div>
        <div className="md:col-span-2"><label className={labelCls}>Summary</label><textarea rows={5} data-testid="profile-summary" className={field} value={p.summary || ""} onChange={(e) => set("summary", e.target.value)} /></div>
        <div><label className={labelCls}>Email</label><input data-testid="profile-email" className={field} value={p.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
        <div><label className={labelCls}>LinkedIn URL</label><input data-testid="profile-linkedin" className={field} value={p.linkedin || ""} onChange={(e) => set("linkedin", e.target.value)} /></div>
        <div><label className={labelCls}>GitHub URL</label><input data-testid="profile-github" className={field} value={p.github || ""} onChange={(e) => set("github", e.target.value)} /></div>
        <div className="md:col-span-2"><label className={labelCls}>Resume URL</label><input data-testid="profile-resume" className={field} value={p.resume_url || ""} onChange={(e) => set("resume_url", e.target.value)} /></div>
        <div className="md:col-span-2"><label className={labelCls}>Photo URL (hero portrait)</label><input data-testid="profile-photo" className={field} value={p.photo || ""} onChange={(e) => set("photo", e.target.value)} /></div>
      </div>
      <button data-testid="profile-save" onClick={save} disabled={saving} className="mt-6 flex items-center gap-2 rounded-sm bg-[var(--text)] px-5 py-3 font-mono text-xs uppercase tracking-widest text-[var(--bg)] disabled:opacity-50">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Profile
      </button>
    </div>
  );
}

export default function Admin() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} (new) | project

  useEffect(() => {
    if (ready && !user) navigate("/login");
  }, [ready, user, navigate]);

  const load = () => api.get("/projects").then(({ data }) => setProjects(data.projects));
  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Project deleted");
    load();
  };

  const afterSave = () => {
    setEditing(null);
    load();
  };

  if (!ready || !user) return null;

  return (
    <div className="relative min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <div className="noise-overlay" />
      <div className="grid min-h-screen grid-cols-1 md:grid-cols-[260px_1fr]">
        {/* sidebar */}
        <aside className="flex flex-col justify-between border-b border-[var(--border)] bg-[var(--surface)] p-6 md:border-b-0 md:border-r">
          <div>
            <p className="font-display text-lg font-black uppercase tracking-tighter">
              Studio<span className="text-[var(--accent)]">.</span>
            </p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-2)]">
              {user.email}
            </p>
            <nav className="mt-8 flex flex-col gap-1">
              <button data-testid="tab-projects" onClick={() => setTab("projects")} className={`flex items-center gap-3 rounded-sm px-4 py-3 text-left font-mono text-xs uppercase tracking-widest transition-colors ${tab === "projects" ? "bg-[var(--text)] text-[var(--bg)]" : "hover:bg-[var(--border)]/40"}`}>
                <LayoutGrid size={15} /> Projects
              </button>
              <button data-testid="tab-profile" onClick={() => setTab("profile")} className={`flex items-center gap-3 rounded-sm px-4 py-3 text-left font-mono text-xs uppercase tracking-widest transition-colors ${tab === "profile" ? "bg-[var(--text)] text-[var(--bg)]" : "hover:bg-[var(--border)]/40"}`}>
                <User size={15} /> Profile
              </button>
            </nav>
          </div>
          <div className="mt-8 flex flex-col gap-2">
            <a href="/" className="rounded-sm border border-[var(--border)] px-4 py-3 text-center font-mono text-[10px] uppercase tracking-widest transition-colors hover:border-[var(--accent)]">
              View Site
            </a>
            <button data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }} className="flex items-center justify-center gap-2 rounded-sm px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[var(--text-2)] hover:text-red-500">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </aside>

        {/* main */}
        <main className="p-6 sm:p-10">
          {tab === "projects" && (
            <>
              <div className="mb-8 flex items-center justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--accent)]">Manage</p>
                  <h1 className="font-display text-3xl font-black uppercase tracking-tight sm:text-4xl">Projects</h1>
                </div>
                {!editing && (
                  <button data-testid="add-project-btn" onClick={() => setEditing({})} className="flex items-center gap-2 rounded-sm bg-[var(--text)] px-5 py-3 font-mono text-xs uppercase tracking-widest text-[var(--bg)]">
                    <Plus size={15} /> Add
                  </button>
                )}
              </div>

              {editing ? (
                <ProjectForm initial={editing.id ? editing : null} onSaved={afterSave} onCancel={() => setEditing(null)} />
              ) : (
                <div className="flex flex-col gap-3">
                  {projects.map((p) => (
                    <div key={p.id} data-testid={`admin-project-${p.id}`} className="flex flex-col gap-3 border border-[var(--border)] bg-[var(--surface)] p-5 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-2)]">{p.category} · {p.subtitle}</p>
                        <p className="mt-1 truncate font-display text-lg font-bold tracking-tight">{p.title}</p>
                        <div className="mt-1 flex items-center gap-3 font-mono text-[10px] text-[var(--text-2)]">
                          {p.pdf_url && <span className="text-[var(--accent)]">PDF</span>}
                          {p.external_url && <span>LINK</span>}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <button data-testid={`edit-project-${p.id}`} onClick={() => setEditing(p)} className="flex items-center gap-1.5 rounded-sm border border-[var(--border)] px-3 py-2 font-mono text-[10px] uppercase tracking-widest hover:border-[var(--accent)]">
                          <Pencil size={13} /> Edit
                        </button>
                        <button data-testid={`delete-project-${p.id}`} onClick={() => remove(p.id)} className="flex items-center gap-1.5 rounded-sm border border-[var(--border)] px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-red-500 hover:border-red-500">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === "profile" && (
            <>
              <div className="mb-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--accent)]">Manage</p>
                <h1 className="font-display text-3xl font-black uppercase tracking-tight sm:text-4xl">Profile</h1>
              </div>
              <ProfileForm />
            </>
          )}
        </main>
      </div>
    </div>
  );
}
